/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import {
  Card,
  CardContent,
  Control,
  ControlHelper,
  Input,
  Label,
  Button,
  PageLoader,
  ControlError,
  Toggle,
  Select,
  SelectOption,
  Modal,
  Header,
  Logo,
  ToastContext,
  Checkbox,
} from "@forge/common";
import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  BASIC_INFO_MAX_CHARS,
  BASIC_INFO_MIN_CHARS,
  IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME,
  LOCALE_CONST,
  RBM_ACCESS_TOKEN_STORAGE_NAME,
} from "../../utils/constants";
import "./styles.scss";
import {
  useCreateDeviceMutation,
  useGetHardwareDetailsQuery,
  useGetAvailableSoftwareVersionsQuery,
  useGetSoftwareVersionDetailsQuery,
  useGetSoftwareVersionDownloadSasUrlQuery,
  useSyncCloudInstallMutation,
} from "../../services/api";
import { setDeviceListUpdated, setHeaderDetails } from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import { ConfigurationVariables } from "../../config";
import { useFlags } from "launchdarkly-react-client-sdk";
import { validateSpecialChar, validateAlphanumeric, validateHardwareModel } from "../../utils/utility";
import { ILanguageContextualization } from "../../models/createdevice";
import LogoutButton from "./LogoutButton";
import { useTranslation } from "react-i18next";
import { OrgListProps } from "../../models";
import { RootState } from "../../store";
import HardwareDetails from "../EBICognition/HardwareDetails";
import SoftwareDetails from "../EBICognition/SoftwareDetails";
import SoftwareDialog from "../SoftwareDialog";
import {
  IAvailableSoftwareVersion,
  ISoftwareVersionDetails,
  ISoftwareVersionDownloadSasUrl,
} from "../../models/softwaredetails";
import {
  SoftwareDialogTableHeader,
  SoftwareDialogTableRow,
} from "../../models/softwareDialog";
import { formatFileSize } from "../../utils/utility";
import { downloadLargeFile } from "../../services/file-service-api/fileDownloader";
import { NetworkErrObj } from "../gateway-report/utils/gatewayTypes";
import { getErrorInfo } from "../../reusablecomponents/errorInfo/getErrorInfo";
import { mapDisconnectedDeviceSoftwareVersions } from "../../utils/mapper";

const CreateDevice = (props: {
  type?: string;
  loadType?: string;
  setClearToast?: (clearToastFn: () => void) => void;
}) => {
  const REGISTRATION_KEY_LENGTH = 6;
  const TIMEOUT_DURATION = 3000;
  const { addSuccess, addError, removeAll } = useContext(ToastContext);

  const flags = useFlags();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Device type options

  const { selectedOrg, siOrgLists } = useSelector(
    (state: RootState) => state.global,
  );
  const convertFn = (value: string) => t(value);
  const [reqObject, setReqObject] = useState<any>({
    name: "",
    registrationKey: "",
    migration: false,
    systemGUID: "",
    isDisconnected: false,
    // language: flags.gatewayLanguageContextualization?.[0]?.value || ''
  });
  const [hardwareData, setHardwareData] = useState({
    deviceType: "",
    hardwareModel: "",
    hardwareID: "",
  });
  const [createDevice, { isLoading }] = useCreateDeviceMutation(); // Mutation hook
  const [syncCloudInstall] = useSyncCloudInstallMutation();
  const { data: hardwareDetailsData, error: hardwareDetailsError, isLoading: isHardwareDetailsTypeLoading } =
    useGetHardwareDetailsQuery(undefined, {
      skip: !reqObject.isDisconnected,
    });
  // First, get availableDeviceTypesFromConfig as an array
  const availableDeviceTypesFromConfig = ConfigurationVariables.ALLOWED_DISCONNECTED_GATEWAY_TYPES?.split(" ") || [];

  // Device type options - intersection of config allowed types and API data
  const availableDeviceTypes = useMemo(() => {
    if (isHardwareDetailsTypeLoading || !hardwareDetailsData) return [];

    // Filter hardwareDetailsData to only include types that are in availableDeviceTypesFromConfig
    return hardwareDetailsData.filter((item: any) =>
      availableDeviceTypesFromConfig.includes(item.deviceType || item)
    );
  }, [hardwareDetailsData, isHardwareDetailsTypeLoading, availableDeviceTypesFromConfig]);

  // Set default device type for disconnected devices
  useEffect(() => {
    if (reqObject.isDisconnected && !isHardwareDetailsTypeLoading && hardwareDetailsData?.length > 0 && !hardwareData.deviceType) {
      const defaultDeviceType = availableDeviceTypes.length > 0
        ? (availableDeviceTypes[0])
        : "";
      setHardwareData(prev => ({ ...prev, deviceType: defaultDeviceType }));
    }
  }, [reqObject.isDisconnected, isHardwareDetailsTypeLoading, hardwareDetailsData, availableDeviceTypes, hardwareData.deviceType]);

  const [selectedVersion, setSelectedVersion] = useState<{
    version: string;
    releaseType: string;
  }>({ version: "", releaseType: "" });
  const [fileToDownload, setFileToDownload] = useState<{
    version: string;
    releaseType: string;
  }>({ version: "", releaseType: "" });
  const [
    selectedAvailableSoftwareVersions,
    setSelectedAvailableSoftwareVersions,
  ] = useState<IAvailableSoftwareVersion[]>([]);
  const [selectedSoftwareVersionDetails, setSelectedSoftwareVersionDetails] =
    useState<ISoftwareVersionDetails>();
  const [selectedSoftwareDownloadDetails, setSelectedSoftwareDownloadDetails] =
    useState<ISoftwareVersionDownloadSasUrl>();
  const [isSoftwareDialogOpen, setIsSoftwareDialogOpen] = useState(false);
  const [softwareDialogMode, setSoftwareDialogMode] = useState<
    "availableVersionDetails" | "details"
  >("details");

  const {
    data: availableSoftwareVersions,
    error: availableSoftwareVersionsError,
    isLoading: availableSoftwareVersionsLoading,
  } = useGetAvailableSoftwareVersionsQuery(
    {
      gatewayType: hardwareData.deviceType,
      packageType: "baseTarVersions",
    },
    { skip: !hardwareData.deviceType },
  );

  const {
    data: softwareVersionDetails,
    error: softwareVersionDetailsError,
    isLoading: softwareVersionDetailsLoading,
  } = useGetSoftwareVersionDetailsQuery(
    {
      gatewayType: hardwareData.deviceType,
      version: selectedVersion.version,
      releaseType: selectedVersion.releaseType,
    },
    {
      skip:
        !hardwareData.deviceType ||
        !selectedVersion.version ||
        !selectedVersion.releaseType,
    },
  );

  const {
    data: softwareVersionDownloadSasUrl,
    error: softwareVersionDownloadSasUrlError,
    isLoading: softwareVersionDownloadSasUrlLoading,
  } = useGetSoftwareVersionDownloadSasUrlQuery(
    {
      gatewayType: hardwareData.deviceType,
      version: fileToDownload.version,
      releaseType: fileToDownload.releaseType,
    },
    {
      skip:
        !hardwareData.deviceType ||
        !fileToDownload.version ||
        !fileToDownload.releaseType,
    },
  );

  useEffect(() => {
    if (availableSoftwareVersions !== undefined) {
      const mappedVersions = mapDisconnectedDeviceSoftwareVersions(
        availableSoftwareVersions,
      );
      setSelectedAvailableSoftwareVersions(mappedVersions);
    } else if (availableSoftwareVersionsError) {
      setSelectedAvailableSoftwareVersions([]);
    }
  }, [availableSoftwareVersions, availableSoftwareVersionsError]);

  useEffect(() => {
    // Handle errors first to prevent showing cached data from previous successful calls
    if (softwareVersionDetailsError) {
      setSelectedSoftwareVersionDetails(undefined);
    } else if (softwareVersionDetails !== undefined && !softwareVersionDetailsLoading) {
      setSelectedSoftwareVersionDetails(softwareVersionDetails);
    }
  }, [softwareVersionDetails, softwareVersionDetailsError, softwareVersionDetailsLoading]);

  useEffect(() => {
    if (softwareVersionDownloadSasUrl) {
      setSelectedSoftwareDownloadDetails(softwareVersionDownloadSasUrl);
    } else if (softwareVersionDownloadSasUrlError) {
      displayErrorToast(
        (softwareVersionDownloadSasUrlError as NetworkErrObj)?.status,
        getErrorObjValue(softwareVersionDownloadSasUrlError as NetworkErrObj),
        false,
      );
      setFileToDownload({ version: "", releaseType: "" });
    }
  }, [softwareVersionDownloadSasUrl, softwareVersionDownloadSasUrlError]);

  // Reset software version details when selectedVersion changes to ensure fresh data
  useEffect(() => {
    setSelectedSoftwareVersionDetails(undefined);
  }, [selectedVersion.version, selectedVersion.releaseType]);

  const [showToast, setShowToast] = useState(false);
  const [showStandaloneSuccessModal, setShowStandaloneSuccessModal] =
    useState(false);
  const [toastType, setToastType] = useState<
    "error" | "success" | "warning" | "progress"
  >("success");
  const [toastContent, setToastContent] = useState("");
  const [title, setTitle] = useState(t(LOCALE_CONST.untitled));
  const { navigateToLandingPage } = useNavigateToLandingPage();
  const [errorState, setErrorState] = useState({
    showNameError: false,
    showRegistrationKeyError: false,
    showValidRegistrationKeyError: false,
    showSystemGUIDError: false,
    showValidSystemGUIDError: false,
    showNameTypeError: false,
    showSoftwareVersionError: false,
  });
  const [hardwareErrorState, setHardwareErrorState] = useState({
    showDeviceTypeError: false,
    showHardwareModelError: false,
    showHardwareIDError: false,
    showValidHardwareIDError: false,
    showHardwareModelTypeError: false,
  });

  const [showSystemGUIDFiled, setShowSystemGUIDFiled] = useState(false);
  const [selectedSoftwareVersion, setSelectedSoftwareVersion] = useState("");

  // To update the error state
  const setError = (field: string, value: boolean) => {
    setErrorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Validation helpers are imported from utils/utility for consistency

  // To update the hardware error state
  const setHardwareError = (field: string, value: boolean) => {
    setHardwareErrorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    if (type === "name") {
      setError("showNameError", false);
      setError("showNameTypeError", false);
      setReqObject({ ...reqObject, name: e.target.value });
      if (/ {2}|%/.test(e.target.value)) {
        setError("showNameTypeError", true);
      }
    } else if (type === "registrationKey") {
      setError("showRegistrationKeyError", false);
      setError("showValidRegistrationKeyError", false);
      setReqObject({ ...reqObject, registrationKey: e.target.value });
      const isValidRegKey =
        e.target.value.length !== REGISTRATION_KEY_LENGTH ||
        e.target.value !== e.target.value.toUpperCase();
      if (
        (isValidRegKey || !validateSpecialChar(e.target.value)) &&
        e.target.value !== ""
      )
        setError("showValidRegistrationKeyError", true);
    } else if (type === "migration") {
      setError("showSystemGUIDError", false);
      if (e.target.checked) {
        setReqObject({ ...reqObject, migration: e.target.checked });
      } else {
        setReqObject({
          ...reqObject,
          migration: e.target.checked,
          systemGUID: "",
        });
      }
    } else if (type === "systemGUID") {
      setError("showSystemGUIDError", false);
      setError("showValidSystemGUIDError", false);
      setReqObject({ ...reqObject, systemGUID: e.target.value });
      if (!validateSpecialChar(e.target.value))
        setError("showValidSystemGUIDError", true);
    }
  };

  // Handle hardware input changes
  const handleHardwareInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    type: string,
  ) => {
    if (type === "deviceType") {
      setHardwareError("showDeviceTypeError", false);
      // Reset hardwareModel when deviceType changes
      setHardwareData({
        ...hardwareData,
        deviceType: e.target.value,
        hardwareModel: "",
      });
    } else if (type === "hardwareModel") {
      setHardwareError("showHardwareModelError", false);
      setHardwareError("showHardwareModelTypeError", false);
      setHardwareData({ ...hardwareData, hardwareModel: e.target.value });
      if (e.target.value !== "" && !validateHardwareModel(e.target.value)) {
        setHardwareError("showHardwareModelTypeError", true);
      }
    } else if (type === "hardwareID") {
      setHardwareError("showHardwareIDError", false);
      setHardwareError("showValidHardwareIDError", false);
      setHardwareData({ ...hardwareData, hardwareID: e.target.value });
      if (e.target.value !== "" && !validateAlphanumeric(e.target.value)) {
        setHardwareError("showValidHardwareIDError", true);
      }
    }
  };

  const handleCancel = () => {
    navigateToLandingPage();
  };

  const handleCreate = async () => {
    // Validate basic info fields
    //validation for disconnected device creation
    if (reqObject.isDisconnected) {
      const hasNameError = reqObject.name === "";
      const hasModelError = hardwareData.hardwareModel === "";
      const hasIDError = hardwareData.hardwareID === "";
      const hasVersionError = selectedSoftwareVersion === "";
      const hasInvalidID =
        hardwareData.hardwareID !== "" &&
        !validateAlphanumeric(hardwareData.hardwareID);
      const hasInvalidModel =
        hardwareData.hardwareModel !== "" &&
        !validateHardwareModel(hardwareData.hardwareModel);

      if (
        hasNameError ||
        hasModelError ||
        hasIDError ||
        hasVersionError ||
        hasInvalidID ||
        hasInvalidModel
      ) {
        setError("showNameError", hasNameError);
        setHardwareError("showHardwareModelError", hasModelError);
        setHardwareError("showHardwareIDError", hasIDError);
        setError("showSoftwareVersionError", hasVersionError);
        setHardwareError("showValidHardwareIDError", hasInvalidID);
        setHardwareError("showHardwareModelTypeError", hasInvalidModel);
        return;
      }
    }

    //validation for connected device creation
    if (!reqObject.isDisconnected) {
      if (
        reqObject.name === "" &&
        reqObject.registrationKey === "" &&
        showSystemGUIDFiled &&
        reqObject.systemGUID === ""
      ) {
        setError("showNameError", true);
        setError("showRegistrationKeyError", true);
        setError("showSystemGUIDError", true);
        return;
      } else if (reqObject.name === "" && reqObject.registrationKey === "") {
        setError("showNameError", true);
        setError("showRegistrationKeyError", true);
        return;
      } else if (reqObject.name === "") {
        setError("showNameError", true);
        return;
      } else if (reqObject.registrationKey === "") {
        setError("showRegistrationKeyError", true);
        return;
      } else if (
        reqObject.registrationKey.length !== REGISTRATION_KEY_LENGTH ||
        reqObject.registrationKey !== reqObject.registrationKey.toUpperCase()
      ) {
        setError("showValidRegistrationKeyError", true);
        return;
      } else if (!validateSpecialChar(reqObject.registrationKey)) {
        setError("showValidRegistrationKeyError", true);
        return;
      } else if (showSystemGUIDFiled && reqObject.systemGUID === "") {
        setError("showSystemGUIDError", true);
        return;
      } else if (
        showSystemGUIDFiled &&
        !validateSpecialChar(reqObject.systemGUID)
      ) {
        setError("showValidSystemGUIDError", true);
        return;
      } else if (
        reqObject.name.length < BASIC_INFO_MIN_CHARS ||
        reqObject.name.length > BASIC_INFO_MAX_CHARS
      ) {
        showErrorToast(t(LOCALE_CONST.gwNameValidation));
        return;
      }
    }

    // If all basic info is valid, proceed with device creation
    // const languageAppend = `(${t(LOCALE_CONST.language)}:${reqObject.language})`;
    try {
      const payload = reqObject.isDisconnected
        ? {
          name: reqObject.name,
          isDisconnected: true,
          gatewayType: hardwareData.deviceType,
          model: hardwareData.hardwareModel,
          hardwareId: hardwareData.hardwareID,
          softwareVersion: selectedSoftwareVersion,
        }
        : {
          name: reqObject.name,
          registrationKey: reqObject.registrationKey,
          migration: reqObject.migration,
          isDisconnected: false,
          ...(reqObject.migration && { systemGuid: reqObject.systemGUID }),
        };
      const result = await createDevice(payload).unwrap();
      if (result) {
        if (reqObject.isDisconnected && selectedSoftwareVersion) {
          const createdDeviceId = result?.deviceId || result?.id;
          if (createdDeviceId) {
            try {
              await syncCloudInstall({
                deviceId: createdDeviceId,
                version: selectedSoftwareVersion,
                status: "installed",
              }).unwrap();
            } catch (error) {
              displayErrorToast(
                (error as NetworkErrObj)?.status,
                getErrorObjValue(error as NetworkErrObj),
                false,
              );
            }
          }
        }
        if (props?.type === "standalone") {
          setShowStandaloneSuccessModal(true);
        } else {
          showSuccessToast(
            t(LOCALE_CONST.gwNameSuccessCreate, {
              gatewayName: reqObject.name.trim(),
            }),
          );
        }
        dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
        setReqObject({
          name: "",
          registrationKey: "",
          migration: false,
          systemGUID: "",
        });
      }
    } catch (error) {
      handleApiError(error); // Handle specific error cases
    }
  };

  useEffect(() => {
    dispatch(
      setHeaderDetails({
        headerDetails: {
          status: "",
          type: "create",
          title: t(LOCALE_CONST.createNewGateway),
        },
      }),
    );
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("oidc.default")) {
      const tokenVal =
        JSON.parse(sessionStorage.getItem("oidc.default") || "{}")?.tokens
          ?.accessToken || "";
      sessionStorage.setItem(RBM_ACCESS_TOKEN_STORAGE_NAME, tokenVal);
      dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
    }
  }, []);

  // Helper functions
  const showSuccessToast = (message: any) => {
    setToastType("success");
    setToastContent(message);
    setShowToast(true);
  };

  const showErrorToast = (message: string) => {
    setToastType("error");
    setToastContent(message);
    setShowToast(true);
  };

  useEffect(() => {
    if (selectedSoftwareDownloadDetails) {
      const performDownload = async () => {
        const { error } = await downloadLargeFile(
          selectedSoftwareDownloadDetails.downloadUrl,
          selectedSoftwareDownloadDetails.fileName || fileToDownload.version,
        );

        if (error) {
          showErrorToast(error);
        }
      };

      performDownload();
    }
    setFileToDownload({ version: "", releaseType: "" });
  }, [selectedSoftwareDownloadDetails]);

  /**
   * To set the error for toast
   * @param statusCode number
   */
  const displayErrorToast = (
    statusCode: number,
    errMsg?: string,
    fetchConfig?: boolean,
  ) => {
    const errObj = getErrorInfo(statusCode, convertFn);
    if (!fetchConfig) {
      showErrorToast(errMsg || errObj.description);
    } else {
      showErrorToast(t(LOCALE_CONST.noRespReceivedSystem));
    }
  };

  const getErrorObjValue = (error: NetworkErrObj) => {
    if (!error) {
      return "";
    } else if (typeof error.data === "object") {
      return error.data?.Message || "";
    } else if (typeof error.data === "string") {
      return error.data || "";
    } else {
      return "";
    }
  };

  const handleApiError = (error: any) => {
    const errorMessages: { [key: number]: string } = {
      400: t(LOCALE_CONST.notSaveGWDetails),
      401: t(LOCALE_CONST.unAuthFails),
      403: t(LOCALE_CONST.AuthForbidFails),
      404: t(LOCALE_CONST.clientError_404),
      409: t(LOCALE_CONST.gwNameAlreadyExists, { gatewayName: reqObject.name }),
      500: t(LOCALE_CONST.internalServerError),
      502: t(LOCALE_CONST.badGatewayTryAgainLater),
      503: t(LOCALE_CONST.serviceUnavailTryAgainLater),
      504: t(LOCALE_CONST.gwTimeoutRequestTryAgainLater),
    };

    if (error?.data?.message?.match?.(/Registration/)) {
      errorMessages[404] = t(LOCALE_CONST["404InvalidRegKey"], {
        regKey: reqObject.registrationKey,
      });
      errorMessages[410] = t(LOCALE_CONST.regKeyExpired);
    }

    showErrorToast(
      errorMessages[error?.status] || t(LOCALE_CONST.somethingWentWrong),
    );
  };

  const clearToast = () => {
    setShowToast(false);
    removeAll(); // Clear all toast notifications
  };

  // Expose the clearToast function to the parent component
  useEffect(() => {
    if (props.setClearToast) {
      props.setClearToast(clearToast);
    }
  }, [props.setClearToast]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        if (props.type !== "standalone") {
          if (toastType === "success") {
            navigateToLandingPage();
            removeAll();
          } else if (toastType === "error") {
            removeAll();
          }
        }
      }, TIMEOUT_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showToast, toastType, props.type]);

  // useEffect(() => {
  //   if (showToast) {
  //     const timer = setTimeout(() => {
  //       setShowToast(false);
  //       if (props.type !== "standalone") {
  //         if (toastType === "success") {
  //           navigateToLandingPage();
  //           removeAll();
  //         } else if (toastType === "error") {
  //           removeAll();
  //         }
  //       }
  //     }, TIMEOUT_DURATION);

  //     return () => clearTimeout(timer);
  //   }
  // }, [showToast, toastType, props.type]);

  useEffect(() => {
    if (showToast) {
      if (toastType === "success") {
        addSuccess({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      } else if (toastType === "error") {
        console.log("error jb", toastContent);
        addError({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      }
    }
  }, [showToast, toastType, toastContent]);

  useEffect(() => {
    if (reqObject.name === "") {
      setTitle(t(LOCALE_CONST.untitled));
    } else {
      setTitle(reqObject.name);
    }
  }, [reqObject.name]);

  const handelMigrationToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowSystemGUIDFiled(!showSystemGUIDFiled);
    handleInputChange(e, "migration");
  };

  // const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.value !== "") {
  //     setReqObject({ ...reqObject, language: event.target.value });
  //     return;
  //   }

  // };

  const renderWarnWindow = () => {
    if (props?.loadType === "noSIOrg") {
      return (
        <Modal title={t(LOCALE_CONST.warning)} className="confirmation-modal">
          <div className="confirmation-modal-content">
            {t(LOCALE_CONST.noOrgAssociated)}
          </div>
        </Modal>
      );
    } else if (props?.loadType === "error") {
      return (
        <Modal title={t(LOCALE_CONST.error)} className="confirmation-modal">
          <div className="confirmation-modal-content">
            {t(LOCALE_CONST.orgListFailed)}
          </div>
          <div className="confirmation-modal-footer">
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t(LOCALE_CONST.refresh)}
            </Button>
          </div>
        </Modal>
      );
    } else {
      return "";
    }
  };

  const onOrgChange = (value: string) => {
    dispatch({
      type: "SET_ORG_LIST",
      payload: {
        selectedOrg: value,
      },
    });
    sessionStorage.setItem(IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME, value);
  };

  // Transform software version details into table format for SoftwareDialog
  const softwareDialogTableHeaders = useMemo<SoftwareDialogTableHeader[]>(
    () => [
      { id: "component", content: t(LOCALE_CONST.component) },
      { id: "version", content: t(LOCALE_CONST.version) },
    ],
    [t],
  );

  const softwareDialogTableData = useMemo<SoftwareDialogTableRow[]>(() => {
    if (!selectedSoftwareVersionDetails?.components) return [];
    return selectedSoftwareVersionDetails.components.map((item, index) => ({
      id: `row-${index}`,
      cell: [{ content: item.component }, { content: item.version }],
    }));
  }, [selectedSoftwareVersionDetails]);

  const headerRightSlot = (
    <div className="header-right-slot">
      <Select
        aria-label="Shape"
        defaultValue={selectedOrg}
        height={5}
        id="shape-select"
        onChange={(e: any) => onOrgChange(e.target.value)}
        placeholder={t(LOCALE_CONST.selectOrg)}
        size="large"
      >
        {siOrgLists.map((item: OrgListProps) => (
          <SelectOption key={item.value} label={item.text} value={item.value} />
        ))}
      </Select>
      <LogoutButton />
    </div>
  );
  const enableEbiCognition = flags.enableEbiCognition;
  return (
    <>
      {renderWarnWindow()}
      <PageLoader
        isLoading={props?.loadType === "loading" || isLoading || false}
      />
      {props?.type === "standalone" && (
        <Header
          logo={<Logo domain={t(LOCALE_CONST.gatewayManagement)} />}
          onMenuClick={function noRefCheck() { }}
          rightSlot={headerRightSlot}
          showMenuToggle={false}
          className="stand-alone-header"
        />
      )}
      <div>
        <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl edit-dev-card-block-cls">
          <span className="iot-basic-info-text p-5 block">
            {t(LOCALE_CONST.basicInfo)}
          </span>
          <CardContent top="none">
            <Control className="mb-4 w-[50%] mt-2" spacingAbove spacingBelow>
              <Label className="font-normal" required requiredText="">
                {t(LOCALE_CONST.name)}
              </Label>
              <Input
                id="name"
                value={reqObject.name}
                onChange={(e) => handleInputChange(e, "name")}
                className={
                  errorState.showNameError || errorState.showNameTypeError
                    ? "error-input-text"
                    : "normal-input"
                }
                error={errorState.showNameError || errorState.showNameTypeError}
                required
                data-testid="test-gateway-name"
                maxLength={50}
              />
              {errorState.showNameTypeError && (
                <ControlError showIcon>
                  {t(LOCALE_CONST.invalidCharacter)}
                </ControlError>
              )}
              {errorState.showNameError && (
                <ControlError showIcon>
                  {t(LOCALE_CONST.requiredField)}
                </ControlError>
              )}
              <ControlHelper className="helper-text">
                {t(LOCALE_CONST.gatewayNameValidText)}
              </ControlHelper>
            </Control>
            {/* Added checkbox for disconnected devices */}
            {enableEbiCognition && (
              <Checkbox
                id="isDisconnected"
                className="mt-2"
                checked={reqObject.isDisconnected}
                data-testid="test-disconnected-checkbox"
                onChange={(e) => {
                  setReqObject({
                    ...reqObject,
                    isDisconnected: e.target.checked,
                  });
                  setSelectedSoftwareVersion("");
                }}
              >
                {t(LOCALE_CONST.disconnectedDeviceCheckedMsg)}
              </Checkbox>
            )}

            {!reqObject.isDisconnected && (
              <div className="connected-gateway-section">
                <Control className="mb-4 w-[50%] mt-2">
                  <Label className="font-normal" required requiredText="">
                    {t(LOCALE_CONST.enterRegKey)}
                  </Label>
                  <Input
                    id="registrationKey"
                    onChange={(e) => handleInputChange(e, "registrationKey")}
                    className={
                      errorState.showRegistrationKeyError
                        ? "error-input-text"
                        : "normal-input"
                    }
                    error={errorState.showRegistrationKeyError}
                    data-testid="test-registration-key"
                    value={reqObject.registrationKey}
                    maxLength={6}
                  />
                  {errorState.showRegistrationKeyError && (
                    <ControlError showIcon>
                      {t(LOCALE_CONST.requiredField)}
                    </ControlError>
                  )}
                  {errorState.showValidRegistrationKeyError && (
                    <ControlError showIcon>
                      {t(LOCALE_CONST.regKeyValidation)}
                    </ControlError>
                  )}
                  <ControlHelper className="helper-text">
                    {t(LOCALE_CONST.manualKeyGenerText)},
                    <a
                      className="text-blue-500"
                      href={`mailto:${ConfigurationVariables.HONEYWELL_SUPPORT_EMAILID}`}
                    >
                      {" "}
                      {t(LOCALE_CONST.contact)}{" "}
                    </a>
                    {t(LOCALE_CONST.systemIntegrator)}
                  </ControlHelper>
                </Control>
                <div
                  className={`w-[34rem] mt-2 p-0 ${showSystemGUIDFiled ? "mb-4" : ""
                    }`}
                >
                  <Label
                    className="font-normal"
                    optional
                    optionalText={`- ${t(LOCALE_CONST.optional)}`}
                  >
                    {t(LOCALE_CONST.existingGateway)}
                  </Label>
                  <p className="helper-text-readable">
                    {t(LOCALE_CONST.upgradeCurrGateway)}
                  </p>
                  <Toggle
                    id="migration"
                    onChange={handelMigrationToggle}
                    data-testid="test-migration"
                  />
                </div>
                {showSystemGUIDFiled && (
                  <div className="w-[50%] mt-2 pb-0">
                    <Label
                      className="font-normal pb-2"
                      required
                      requiredText=""
                    >
                      {t(LOCALE_CONST.enterSystemGUID)}
                    </Label>
                    <Input
                      id="systemGUID"
                      className={
                        errorState.showSystemGUIDError
                          ? "error-input-text"
                          : "normal-input"
                      }
                      onChange={(e) => handleInputChange(e, "systemGUID")}
                      error={errorState.showSystemGUIDError}
                      data-testid="test-system-guid"
                      value={reqObject.systemGUID}
                      maxLength={50}
                    />
                    {errorState.showSystemGUIDError && (
                      <ControlError showIcon>
                        {t(LOCALE_CONST.requiredField)}
                      </ControlError>
                    )}
                    {errorState.showValidSystemGUIDError && (
                      <ControlError showIcon>
                        {t(LOCALE_CONST.enterValidSystemGUID)}
                      </ControlError>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* <Control className="w-[34rem] mt-2 pb-0" >
              <Label>{t(LOCALE_CONST.langNameConvention)}</Label>
              <Select aria-label="Language" value={reqObject.language} multiple={false} id="language-select" onChange={handleLanguageChange} data-testid="test-language">
                {flags.gatewayLanguageContextualization?.map((language: ILanguageContextualization) => (
                  <SelectOption key={language?.value} value={language?.value} label={language?.label} />
                ))}
              </Select>
            </Control> */}
          </CardContent>
        </Card>
        {reqObject.isDisconnected && (
          <HardwareDetails
            hardwareData={hardwareData}
            errorState={hardwareErrorState}
            deviceTypeOptions={availableDeviceTypes}
            onInputChange={handleHardwareInputChange}
            isLoading={isHardwareDetailsTypeLoading}
          />
        )}
      </div>

      {/* Software Details Component - Only for Disconnected Devices */}
      {reqObject.isDisconnected && !isHardwareDetailsTypeLoading && (
        <div className="mt-4">
          <SoftwareDetails
            availableSoftwareVersions={selectedAvailableSoftwareVersions}
            isLoading={availableSoftwareVersionsLoading}
            isCreate={true}
            showVersionError={errorState.showSoftwareVersionError}
            showDownload={true}
            onVersionChange={(version, releaseType) => {
              setSelectedSoftwareVersion(version);
              setError("showSoftwareVersionError", false);
            }}
            onDownload={(version, releaseType) => {
              setFileToDownload({ version, releaseType });
            }}
            onViewDetails={(version, releaseType) => {
              setSelectedVersion({ version, releaseType });
              setSoftwareDialogMode("details");
              setIsSoftwareDialogOpen(true);
            }}
            onVersionCardClick={(version, releaseType) => {
              setSelectedVersion({ version, releaseType });
              setSoftwareDialogMode("availableVersionDetails");
              setIsSoftwareDialogOpen(true);
            }}
          />
        </div>
      )}

      {/* Software Dialog */}
      {reqObject.isDisconnected && (
        <SoftwareDialog
          isOpen={isSoftwareDialogOpen}
          onClose={() => setIsSoftwareDialogOpen(false)}
          softwareVersion={selectedVersion.version}
          downloadSize={formatFileSize(
            selectedSoftwareVersionDetails?.size || "0",
          )}
          tableHeaders={softwareDialogTableHeaders}
          tableData={softwareDialogTableData}
          showFooterText={softwareDialogMode === "availableVersionDetails"}
          showDownload={false}
        />
      )}

      <div className="create-device-button-Container flex space-x-4 mt-6">
        {props?.type !== "standalone" && (
          <Button variant="secondary" onClick={handleCancel}>
            {t(LOCALE_CONST.cancel)}
          </Button>
        )}
        <Button variant="primary" onClick={handleCreate}>
          {t(LOCALE_CONST.save)}
        </Button>
      </div>
      {/* Confirmation Modal for Standalone only */}
      {props?.type === "standalone" && showStandaloneSuccessModal && (
        <Modal title="" className="standalone-confirmation-modal">
          <div className="modal-content text-center">
            <div className="flex justify-center">
              <img
                src="/img/standalone-success.svg"
                alt="Standalone Success"
                className="standalone-success-img"
              />
            </div>
            <div className="modal-title">
              <h3 className="pt-4 pb-4">
                {t(LOCALE_CONST.gatewayConfirmationTitle)}
              </h3>
            </div>
            <div className="modal-subtitle">
              <p>{t(LOCALE_CONST.gatewayConfirmationText)}</p>
            </div>

            <div className="modal-footer-container">
              <LogoutButton isModalButton={true} />
              <Button
                variant="primary"
                onClick={() => {
                  setShowStandaloneSuccessModal(false);
                }}
                size="large"
              >
                Register New gateway
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CreateDevice;
